import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MOCK_USERS } from './userQueries';
import { mockWorkflowRules } from './workflowQueries';

// ============================================
// MOCK DATA (Replace with API calls later)
// ============================================

const mockQuickActions = [
  {
    id: 'add-user',
    icon: 'UserPlus',
    title: 'Add User',
    description: 'Create new team member',
    action: '/admin/users/new'
  },
  {
    id: 'new-field',
    icon: 'Plus',
    title: 'New Custom Field',
    description: 'Add field to any object',
    action: '/admin/object-manager'
  },
  {
    id: 'email-template',
    icon: 'Mail',
    title: 'Create Email Template',
    description: 'Design email template',
    action: '/admin/email-templates/new'
  },
  {
    id: 'configure-roles',
    icon: 'Shield',
    title: 'Configure Roles',
    description: 'Manage permissions',
    action: '/admin/roles'
  },
  {
    id: 'approval-processes',
    icon: 'CheckSquare',
    title: 'Approval Processes',
    description: 'Configure approval workflows',
    action: 'approval-processes'
  }
];

const mockSetupChanges = [
  {
    id: 'rc_001',
    user: 'Christina van Tillo',
    action: 'updated user role',
    target: 'Emily Davis to Senior Analyst',
    timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 minutes ago
    type: 'user_updated'
  },
  {
    id: 'rc_002',
    user: 'Christina van Tillo',
    action: 'created user',
    target: 'David Wilson',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
    type: 'user_created'
  },
  {
    id: 'rc_003',
    user: 'Christina van Tillo',
    action: 'activated user',
    target: 'Michael Brown',
    timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), // 4 hours ago
    type: 'user_activated'
  },
  {
    id: '2',
    user: 'Admin',
    action: 'added custom field',
    target: 'Contract_Value__c to Client object',
    timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(), // 5 hours ago
    type: 'field_created'
  },
  {
    id: '3',
    user: 'Mike Davis',
    action: 'modified page layout',
    target: 'Client Standard Layout',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
    type: 'layout_modified'
  },
  {
    id: '4',
    user: 'Sarah Johnson',
    action: 'created email template',
    target: 'Welcome Email',
    timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
    type: 'template_created'
  }
];

const mockSetupTasks = [
  {
    id: 'task-1',
    title: 'Configure CyberSweep settings',
    completed: true,
    order: 1
  },
  {
    id: 'task-2',
    title: 'Add company information',
    completed: true,
    order: 2
  },
  {
    id: 'task-3',
    title: 'Add your team members',
    completed: false,
    order: 3
  },
  {
    id: 'task-4',
    title: 'Customize Client page layout',
    completed: false,
    order: 4
  },
  {
    id: 'task-5',
    title: 'Create email templates',
    completed: false,
    order: 5
  }
];

// Function to get dynamic setup stats
const getSetupStats = () => ({
  activeUsers: MOCK_USERS.filter(u => u.isActive).length, // Dynamic count from userQueries
  customObjects: 4,
  customFields: 23,
  activeWorkflows: mockWorkflowRules.filter(w => w.isActive).length, // Dynamic count from workflowQueries
  storageUsedGB: 12.4,
  storageTotalGB: 100
});

const mockRecentlyViewed = [
  {
    id: '1',
    path: 'Object Manager › Client › Fields',
    timestamp: new Date(Date.now() - 10 * 60 * 1000).toISOString() // 10 min ago
  },
  {
    id: '2',
    path: 'User Management',
    timestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString() // 1 hour ago
  },
  {
    id: '3',
    path: 'Email Templates',
    timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString() // 2 days ago
  }
];

// ============================================
// QUERY KEYS
// ============================================

export const setupKeys = {
  all: ['setup'] as const,
  quickActions: () => [...setupKeys.all, 'quick-actions'] as const,
  recentChanges: () => [...setupKeys.all, 'recent-changes'] as const,
  setupTasks: () => [...setupKeys.all, 'setup-tasks'] as const,
  stats: () => [...setupKeys.all, 'stats'] as const,
  recentlyViewed: () => [...setupKeys.all, 'recently-viewed'] as const,
};

// ============================================
// HOOKS
// ============================================

/**
 * Get quick action cards for Setup Home
 * Future: Replace with API call to GET /api/setup/quick-actions
 */
export function useQuickActions() {
  return useQuery({
    queryKey: setupKeys.quickActions(),
    queryFn: async () => {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 300));
      return mockQuickActions;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Get recent setup changes
 * Future: Replace with API call to GET /api/setup/recent-changes?limit=10
 */
export function useRecentSetupChanges(limit: number = 10) {
  return useQuery({
    queryKey: [...setupKeys.recentChanges(), limit],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 200));
      return mockSetupChanges.slice(0, limit);
    },
    staleTime: 2 * 60 * 1000, // 2 minutes - refresh more often
  });
}

/**
 * Get setup tasks checklist
 * Future: Replace with API call to GET /api/setup/tasks
 */
export function useSetupTasks() {
  return useQuery({
    queryKey: setupKeys.setupTasks(),
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 250));
      return mockSetupTasks;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get setup overview statistics
 * Future: Replace with API call to GET /api/setup/stats
 */
export function useSetupStats() {
  return useQuery({
    queryKey: setupKeys.stats(),
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 400));
      return getSetupStats(); // Call function to get dynamic stats
    },
    staleTime: 1 * 60 * 1000, // Reduced to 1 minute for more reactive updates
  });
}

/**
 * Get recently viewed setup pages
 * Future: Replace with API call to GET /api/setup/recently-viewed
 */
export function useRecentlyViewed() {
  return useQuery({
    queryKey: setupKeys.recentlyViewed(),
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 200));
      return mockRecentlyViewed;
    },
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Toggle setup task completion
 * Future: Replace with API call to PATCH /api/setup/tasks/:id
 */
export function useToggleSetupTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, completed }: { taskId: string; completed: boolean }) => {
      await new Promise(resolve => setTimeout(resolve, 300));

      // Update mock data
      const task = mockSetupTasks.find(t => t.id === taskId);
      if (task) {
        task.completed = completed;
      }

      return { taskId, completed };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: setupKeys.setupTasks() });
    },
  });
}

/**
 * Add page to recently viewed
 * Future: Replace with API call to POST /api/setup/recently-viewed
 */
export function useAddRecentlyViewed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ path }: { path: string }) => {
      await new Promise(resolve => setTimeout(resolve, 100));

      // Add to mock data
      mockRecentlyViewed.unshift({
        id: Date.now().toString(),
        path,
        timestamp: new Date().toISOString()
      });

      // Keep only last 10
      if (mockRecentlyViewed.length > 10) {
        mockRecentlyViewed.pop();
      }

      return { path };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: setupKeys.recentlyViewed() });
    },
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Format timestamp to relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(timestamp: string): string {
  const now = new Date();
  const past = new Date(timestamp);
  const diffMs = now.getTime() - past.getTime();

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 60) {
    return `${diffMinutes} min ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  } else {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }
}

/**
 * Calculate completion percentage for setup tasks
 */
export function calculateTaskCompletion(tasks: typeof mockSetupTasks) {
  const completed = tasks.filter(t => t.completed).length;
  const total = tasks.length;
  const percentage = Math.round((completed / total) * 100);

  return { completed, total, percentage };
}
